import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FieldSeparator } from '@/components/ui/field';

import { supabase } from '../lib/supabase.ts';

interface GoogleSignInButtonProps {
  /** "Sign in" or "Sign up" — Google does not distinguish, but the copy should. */
  label: string;
  /** Surfaces a failure to start the redirect in the parent form's error slot. */
  onError: (message: string) => void;
}

/**
 * The "Or continue with" row from shadcn's login-04 block, cut down to Google.
 *
 * `signInWithOAuth` sends the browser to Google and back to `redirectTo` with a
 * `?code=`; the client was built with `flowType: 'pkce'` and
 * `detectSessionInUrl`, so it exchanges that code on load and `AuthProvider`'s
 * listener swaps the screen for the console. No callback route is needed.
 *
 * The origin has to be listed in `additional_redirect_urls` for the stack in
 * use, or Supabase silently falls back to `site_url`.
 *
 * Sign-up and sign-in are the same call: a first-time Google account becomes a
 * confirmed user with no memberships, exactly like a confirmed email sign-up.
 */
export function GoogleSignInButton({ label, onError }: GoogleSignInButtonProps) {
  const [isRedirecting, setIsRedirecting] = useState(false);

  async function handleClick() {
    setIsRedirecting(true);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });

    // On success the page is already navigating away; only a failure to start
    // the flow ever reaches this line.
    if (error) {
      onError(error.message);
      setIsRedirecting(false);
    }
  }

  return (
    <>
      <FieldSeparator>Or continue with</FieldSeparator>
      <Field>
        <Button type="button" variant="outline" onClick={handleClick} disabled={isRedirecting}>
          <GoogleLogo />
          {isRedirecting ? 'Redirecting to Google…' : `${label} with Google`}
        </Button>
      </Field>
    </>
  );
}

function GoogleLogo() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
        fill="currentColor"
      />
    </svg>
  );
}
